import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { AlertCircle, ArrowLeft, CheckCircle, CreditCard, FileText, Loader2, Mail, MapPin, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { CheckoutAuthGate } from '@/pages/order/CheckoutAuthGate';
import { CheckoutPasswordSetup } from '@/pages/order/CheckoutPasswordSetup';
import {
  buildItemsSummary,
  buildPendingPayment,
  clearPendingPayment,
  getPaymentModeLabel,
  readPendingPayment,
  savePendingPayment,
  storeConfirmation,
} from '@/pages/order/paymentFlow';
import { clearCheckoutResume, readCheckoutResume, updateCheckoutResume } from '@/pages/order/checkoutResume';
import { loadPayPalSdk } from '@/pages/order/paypalLoader';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN',
  'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT',
  'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'PR', 'GU', 'VI', 'AS', 'MP', 'AA', 'AE', 'AP',
];

function paymentBadgeStyles(mode) {
  if (mode === 'stripe_live') {
    return { background: 'rgba(163,106,58,0.12)', color: '#A36A3A' };
  }

  return { background: 'rgba(14,107,93,0.10)', color: '#0E6B5D' };
}

function buildOrderItems(items) {
  return items.map((item) => ({
    service_family: item.service_family,
    category_slug: item.category_slug,
    brand_slug: item.brand_slug || undefined,
    item_title: item.item_title,
    serial_number: item.serial_number,
    description: item.description,
    is_expedited: item.is_expedited,
    wants_mailed_document: item.wants_mailed_document,
    image_keys: item.image_keys || [],
    extra_image_keys: item.extra_image_keys || [],
    appraisal_purpose: item.appraisal_purpose || undefined,
    appraisal_condition: item.appraisal_condition || undefined,
    appraisal_provenance: item.appraisal_provenance || undefined,
  }));
}

function buildConfirmationPayload({ orderId, orderNumber, totalCents, items, pendingPayment }) {
  return {
    order_id: orderId,
    order_number: orderNumber || pendingPayment?.order_number || orderId,
    total_cents: totalCents || pendingPayment?.total_cents || 0,
    items_count: pendingPayment?.items_count || items.length,
    items_summary: pendingPayment?.items_summary || buildItemsSummary(items),
  };
}

function StripePaymentSection({ paymentSession, paymentMode, stripePromise, onPaymentSuccess, onPaymentError }) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: paymentSession.client_secret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0E6B5D',
            borderRadius: '12px',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
          },
        },
      }}
    >
      <StripePaymentForm
        paymentSession={paymentSession}
        paymentMode={paymentMode}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
      />
    </Elements>
  );
}

function StripePaymentForm({ paymentSession, paymentMode, onPaymentSuccess, onPaymentError }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  const handleConfirmPayment = async () => {
    if (!stripe || !elements) {
      const message = 'Secure payment form is still loading. Please wait a moment.';
      onPaymentError(message);
      toast.error(message);
      return;
    }

    setConfirming(true);
    onPaymentError(null);

    const fallbackParams = new URLSearchParams({
      order_id: paymentSession.order_id,
      payment_intent_client_secret: paymentSession.client_secret,
    });

    try {
      const returnUrl = new URL('/order/payment-status', window.location.origin);
      returnUrl.searchParams.set('order_id', paymentSession.order_id);

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl.toString() },
        redirect: 'if_required',
      });

      if (error) {
        const message = error.message || 'Payment confirmation failed.';
        onPaymentError(message);
        toast.error(message);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Target the exact attempt so confirm can't strand the order if the active
        // pointer was cleared (CHK-08).
        const confirmBody = {};
        if (paymentSession.payment_id) confirmBody.payment_id = paymentSession.payment_id;
        else if (paymentSession.payment_intent_id) confirmBody.payment_intent_id = paymentSession.payment_intent_id;
        const finalizeResponse = await api.post(`/orders/${paymentSession.order_id}/confirm-payment`, confirmBody);
        if (finalizeResponse.data?.success) {
          onPaymentSuccess(
            paymentSession.order_id,
            finalizeResponse.data.order_number || paymentSession.order_number,
            finalizeResponse.data.total_charged_cents || paymentSession.total_cents,
          );
          return;
        }

        navigate(`/order/payment-status?${fallbackParams.toString()}`, { replace: true });
        return;
      }

      if (paymentIntent?.status === 'processing') {
        navigate(`/order/payment-status?${fallbackParams.toString()}`, { replace: true });
        return;
      }

      const message = paymentIntent?.status
        ? `Payment not completed. Status: ${paymentIntent.status}`
        : 'Payment confirmation is still in progress.';
      onPaymentError(message);
      toast.error(message);
    } catch (error) {
      // Slice 7.1: broaden error capture. `stripe.confirmPayment` can throw on
      // network errors (no `error.response`), and the /confirm-payment API can
      // return structured details. Prefer the API detail, then a stripe.error
      // message, then a network-friendly fallback.
      let message =
        error?.response?.data?.detail ||
        error?.error?.message ||
        error?.message ||
        'Payment confirmation failed. Please try again.';
      // Never leak stack-like content to the user.
      if (typeof message !== 'string') {
        message = 'Payment confirmation failed. Please try again.';
      }
      onPaymentError(message);
      toast.error(message);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-stone/30 bg-white p-4" data-testid="checkout-payment-element-container">
        <PaymentElement />
      </div>
      {paymentMode === 'stripe_test' && (
        <p className="text-[10px] text-muted-foreground" data-testid="checkout-payment-mode-hint">
          Test card: 4242 4242 4242 4242 &middot; Any future date &middot; Any CVC &middot; Any ZIP
        </p>
      )}
      <Button
        onClick={handleConfirmPayment}
        disabled={confirming || !stripe || !elements}
        className="gap-2 rounded-[12px] h-11 px-8 active:scale-[0.98]"
        data-testid="checkout-confirm-payment-button"
      >
        {confirming ? <><Loader2 className="h-4 w-4 animate-spin" /> Confirming payment...</> : <><CheckCircle className="h-4 w-4" /> Complete Payment</>}
      </Button>
    </div>
  );
}

function PayPalPaymentSection({
  config,
  createPayPalOrder,
  onPaymentSuccess,
  onPaymentError,
}) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const buttonsRef = useRef(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadPayPalSdk(config.paypal_client_id, { currency: 'USD', intent: 'capture' })
      .then(() => { if (!cancelled) setSdkReady(true); })
      .catch((error) => { if (!cancelled) setSdkError(error.message || 'PayPal failed to load.'); });
    return () => { cancelled = true; };
  }, [config.paypal_client_id]);

  useEffect(() => {
    if (!sdkReady || !window.paypal || !containerRef.current) return undefined;

    // Track which order this Buttons instance is driving so onApprove can confirm it.
    const sessionRef = { current: null };

    const buttons = window.paypal.Buttons({
      style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
      // Create the order on OUR backend (provider=paypal), returning the PayPal order id.
      createOrder: async () => {
        try {
          const session = await createPayPalOrder();
          sessionRef.current = session;
          if (!session?.payment_intent_id) {
            throw new Error('PayPal order was not created.');
          }
          return session.payment_intent_id;
        } catch (error) {
          const message = error.response?.data?.detail || error.message || 'Could not start PayPal checkout.';
          onPaymentError(message);
          toast.error(message);
          throw error;
        }
      },
      // Capture happens server-side via confirm-payment (capture-at-checkout).
      onApprove: async () => {
        const session = sessionRef.current;
        try {
          const confirmBody = {};
          if (session?.payment_id) confirmBody.payment_id = session.payment_id;
          else if (session?.payment_intent_id) confirmBody.payment_intent_id = session.payment_intent_id;
          const finalizeResponse = await api.post(`/orders/${session.order_id}/confirm-payment`, confirmBody);
          if (finalizeResponse.data?.success || finalizeResponse.data?.payment_status === 'succeeded') {
            onPaymentSuccess(
              session.order_id,
              finalizeResponse.data.order_number || session.order_number,
              finalizeResponse.data.total_charged_cents || session.total_cents,
            );
            return;
          }
          const fallbackParams = new URLSearchParams({ order_id: session.order_id });
          navigate(`/order/payment-status?${fallbackParams.toString()}`, { replace: true });
        } catch (error) {
          const message = error.response?.data?.detail || error.message || 'PayPal payment could not be finalized.';
          onPaymentError(message);
          toast.error(message);
        }
      },
      onError: (error) => {
        const message = (error && error.message) || 'PayPal encountered an error. Please try again.';
        onPaymentError(message);
        toast.error(message);
      },
      onCancel: () => {
        onPaymentError('PayPal checkout was cancelled.');
      },
    });

    buttonsRef.current = buttons;
    if (buttons.isEligible && !buttons.isEligible()) {
      setSdkError('PayPal is not available for this configuration.');
      return undefined;
    }
    buttons.render(containerRef.current).catch(() => {});

    return () => {
      try { buttonsRef.current?.close?.(); } catch (_) { /* ignore */ }
      buttonsRef.current = null;
    };
  }, [sdkReady, createPayPalOrder, onPaymentSuccess, onPaymentError, navigate]);

  if (sdkError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="checkout-paypal-unavailable-panel">
        <p className="text-sm" style={{ color: '#5A1F2A' }}>{sdkError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="checkout-paypal-section">
      {!sdkReady && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading PayPal...
        </div>
      )}
      <div ref={containerRef} data-testid="checkout-paypal-buttons-container" />
    </div>
  );
}

export default function CheckoutPage() {
  const [config, setConfig] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    api.get('/config').then((response) => {
      setConfig(response.data);
      const providers = response.data.available_providers || [];
      const stripeEnabled = providers.includes('stripe') || response.data.payment_mode?.startsWith('stripe');
      if (response.data.payment_mode !== 'mock' && stripeEnabled && response.data.stripe_publishable_key) {
        setStripePromise(loadStripe(response.data.stripe_publishable_key));
      }
    }).catch(() => {});
  }, []);

  if (!config) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <CheckoutFlow config={config} stripePromise={stripePromise} />;
}

function CheckoutFlow({ config, stripePromise }) {
  const { items, getSubtotal, getTotal, couponCode, couponDiscount, clearCart, replaceCart } = useCart();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const submittingRef = useRef(false);
  const pendingPayment = useMemo(() => readPendingPayment(), []);
  const [checkoutResume, setCheckoutResume] = useState(() => readCheckoutResume());
  const restoredShippingRef = useRef(false);
  const resumedToastRef = useRef(false);
  const cartResumeRef = useRef(false);

  // Feb 2026 abandonment flow: if the email deep-link carries ?resume=<order_id>,
  // rehydrate the cart from the server's pending order once (per mount). We
  // remove the query param after fetch so refreshes don't re-fetch.
  useEffect(() => {
    if (cartResumeRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const resumeId = params.get('resume');
    if (!resumeId || !isAuthenticated) return;
    cartResumeRef.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/orders/pending/resume/${resumeId}`);
        replaceCart(data.items || []);
        toast.success('Your cart has been restored — complete checkout to finish.');
        params.delete('resume');
        const q = params.toString();
        window.history.replaceState({}, '', `/checkout${q ? `?${q}` : ''}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not restore that order.');
      }
    })();
  }, [isAuthenticated, replaceCart]);

  const paymentMode = config.payment_mode || 'mock';
  const availableProviders = useMemo(() => config.available_providers || [], [config.available_providers]);
  const stripeAvailable = availableProviders.includes('stripe') || paymentMode.startsWith('stripe');
  const paypalAvailable = availableProviders.includes('paypal') && Boolean(config.paypal_client_id);

  const [selectedProvider, setSelectedProvider] = useState(() => {
    if (stripeAvailable) return 'stripe';
    if (paypalAvailable) return 'paypal';
    return 'mock';
  });

  const usesStripe = selectedProvider === 'stripe' && paymentMode !== 'mock';
  const usesPayPal = selectedProvider === 'paypal' && paypalAvailable;
  const stripeReady = Boolean(stripePromise);

  const [processing, setProcessing] = useState(false);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [paymentSession, setPaymentSession] = useState(() => pendingPayment || null);
  const [addr, setAddr] = useState({ line_1: '', line_2: '', city: '', state: '', postal_code: '', country: 'US' });

  const needsShipping = items.some((item) => item.wants_mailed_document);
  const inputsLocked = Boolean(paymentSession);
  const requiresPasswordSetup = isAuthenticated && !user?.has_password && checkoutResume?.needs_password_setup;

  useSeoMeta({ seo_title: 'Checkout - The Fashion Detective' });

  useEffect(() => {
    setCheckoutResume(readCheckoutResume());
  }, [isAuthenticated, user?.has_password]);

  useEffect(() => {
    if (restoredShippingRef.current || !checkoutResume?.shipping_address) return;
    setAddr((current) => {
      if (current.line_1 || current.city || current.state || current.postal_code) return current;
      return { ...current, ...checkoutResume.shipping_address };
    });
    restoredShippingRef.current = true;
  }, [checkoutResume]);

  useEffect(() => {
    if (!isAuthenticated || checkoutResume?.resumed_via !== 'magic_link' || resumedToastRef.current) return;
    toast.success('Checkout resumed after your magic-link sign-in.');
    resumedToastRef.current = true;
  }, [checkoutResume, isAuthenticated]);

  const handleCheckoutError = (message) => {
    if (!message) {
      setCheckoutError(null);
      return;
    }

    setCheckoutError(message);
  };

  const handleSuccess = (orderId, orderNumber, totalCents) => {
    const confirmationPayload = buildConfirmationPayload({
      orderId,
      orderNumber,
      totalCents: totalCents || getTotal(),
      items,
      pendingPayment: readPendingPayment() || paymentSession,
    });

    clearPendingPayment();
    clearCheckoutResume();
    storeConfirmation(confirmationPayload);
    setCheckoutComplete(true);
    navigate(`/order/confirmation/${orderId}`, { replace: true });
    setTimeout(() => { clearCart(); }, 0);
    toast.success('Order placed successfully!');
  };

  const validateCheckoutInputs = () => {
    if (!isAuthenticated) return 'Please sign in to continue checkout.';
    if (requiresPasswordSetup) return 'Save a password to continue checkout.';
    if (needsShipping) {
      if (!addr.line_1.trim()) return 'Street address required';
      if (!addr.city.trim()) return 'City required';
      if (!addr.state) return 'State required';
      if (!addr.postal_code.trim()) return 'ZIP required';
    }
    if (usesStripe && !stripeReady) return 'Stripe is not ready yet. Please refresh and try again.';
    return '';
  };

  const handleInitializePayment = async () => {
    if (submittingRef.current || processing) return;

    const validationMessage = validateCheckoutInputs();
    if (validationMessage) {
      handleCheckoutError(validationMessage);
      toast.error(validationMessage);
      return;
    }

    submittingRef.current = true;
    setProcessing(true);
    handleCheckoutError(null);

    try {
      const createResponse = await api.post('/orders', {
        items: buildOrderItems(items),
        coupon_code: couponCode || undefined,
        shipping_address: needsShipping ? addr : undefined,
      });

      updateCheckoutResume({ shipping_address: addr, resumed_via: checkoutResume?.resumed_via || null, needs_password_setup: requiresPasswordSetup });

      const orderId = createResponse.data.order_id;
      const checkoutResponse = await api.post(`/orders/${orderId}/checkout`, usesStripe ? { provider: 'stripe' } : {});

      // BUG-01: a $0 order (100%-off coupon) or the mock driver returns success
      // synchronously with no client_secret — handle those before any provider branch.
      if (checkoutResponse.data.success) {
        handleSuccess(
          orderId,
          createResponse.data.order_number || checkoutResponse.data.order_number,
          checkoutResponse.data.total_charged_cents ?? 0,
        );
        return;
      }

      if (usesStripe) {
        const clientSecret = checkoutResponse.data.client_secret;
        if (!clientSecret) {
          throw new Error('Payment could not be initialized — please try again.');
        }

        const nextPaymentSession = buildPendingPayment({
          orderId,
          orderNumber: createResponse.data.order_number || checkoutResponse.data.order_number || orderId,
          totalCents: createResponse.data.total_cents || getTotal(),
          clientSecret,
          paymentIntentId: checkoutResponse.data.payment_intent_id,
          paymentId: checkoutResponse.data.payment_id,
          items,
        });

        setPaymentSession(nextPaymentSession);
        savePendingPayment(nextPaymentSession);
        toast.success('Order created. Enter your payment details to finish checkout.');
        return;
      }

      throw new Error(checkoutResponse.data.error || 'Checkout failed.');
    } catch (error) {
      const raw = error.response?.data?.detail;
      let message;
      if (typeof raw === 'string') message = raw;
      else if (Array.isArray(raw)) message = raw.map((entry) => (typeof entry === 'object' ? (entry.msg || JSON.stringify(entry)) : String(entry))).join('; ');
      else if (raw && typeof raw === 'object') message = raw.msg || raw.message || JSON.stringify(raw);
      else message = error.message || 'Checkout failed. Please try again.';

      handleCheckoutError(message);
      toast.error(message);
    } finally {
      setProcessing(false);
      submittingRef.current = false;
    }
  };

  // Keep the latest checkout inputs in a ref so the PayPal createOrder callback (which
  // lives inside a stable Buttons instance) always reads current cart/address state.
  const paypalContextRef = useRef({});
  paypalContextRef.current = { items, couponCode, needsShipping, addr, getTotal, requiresPasswordSetup, checkoutResume };

  const createPayPalOrder = useCallback(async () => {
    // Bug 2 fix: guard against double-submit like the Stripe path does.
    // The PayPal SDK can re-invoke createOrder on transient errors or fast
    // repeat clicks — without the ref guard each invocation posts a NEW order
    // to /api/orders, creating phantom duplicates in the staff queue.
    if (submittingRef.current) {
      throw new Error('Order is already being created — please wait a moment.');
    }
    submittingRef.current = true;
    try {
      const ctx = paypalContextRef.current;
      const validationMessage = validateCheckoutInputs();
      if (validationMessage) {
        handleCheckoutError(validationMessage);
        toast.error(validationMessage);
        throw new Error(validationMessage);
      }
      handleCheckoutError(null);

      const createResponse = await api.post('/orders', {
        items: buildOrderItems(ctx.items),
        coupon_code: ctx.couponCode || undefined,
        shipping_address: ctx.needsShipping ? ctx.addr : undefined,
      });
      updateCheckoutResume({
        shipping_address: ctx.addr,
        resumed_via: ctx.checkoutResume?.resumed_via || null,
        needs_password_setup: ctx.requiresPasswordSetup,
      });

      const orderId = createResponse.data.order_id;
      const checkoutResponse = await api.post(`/orders/${orderId}/checkout`, { provider: 'paypal' });
      if (!checkoutResponse.data?.payment_intent_id) {
        throw new Error('PayPal order was not created.');
      }
      return {
        order_id: orderId,
        order_number: createResponse.data.order_number || checkoutResponse.data.order_number || orderId,
        total_cents: createResponse.data.total_cents || ctx.getTotal(),
        payment_intent_id: checkoutResponse.data.payment_intent_id,
        payment_id: checkoutResponse.data.payment_id,
      };
    } finally {
      submittingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (items.length === 0 && !checkoutComplete && !paymentSession) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h1 className="font-display text-2xl font-medium text-ink mb-2">Your cart is empty</h1>
        <p className="text-sm text-muted-foreground mb-4">Add items before checking out.</p>
        <Button variant="outline" onClick={() => navigate('/order/cart')} className="gap-2" data-testid="checkout-back-to-cart-empty-button">
          <ArrowLeft className="h-4 w-4" /> Back to Cart
        </Button>
      </div>
    );
  }

  if (checkoutComplete) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="h-10 w-10 mx-auto mb-3" style={{ color: '#0E6B5D' }} />
          <p className="text-sm text-muted-foreground">Redirecting to your confirmation...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <section style={{ background: 'linear-gradient(135deg, hsl(36 33% 98%) 0%, hsl(36 22% 94%) 55%, hsl(35 19% 88%) 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-2">
          <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-[-0.02em] text-ink">Checkout</h1>
          <p className="text-sm text-muted-foreground" data-testid="checkout-total-summary">{items.length} item{items.length !== 1 ? 's' : ''} - ${(getTotal() / 100).toFixed(2)}</p>
          <Badge className="w-fit text-[10px]" style={paymentBadgeStyles(paymentMode)} data-testid="checkout-payment-mode-badge">
            {getPaymentModeLabel(paymentMode)}
          </Badge>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="space-y-6">
          {!isAuthenticated && <CheckoutAuthGate shippingAddress={addr} />}

          {requiresPasswordSetup && (
            <CheckoutPasswordSetup
              onComplete={() => {
                updateCheckoutResume({ ...(checkoutResume || {}), needs_password_setup: false });
                setCheckoutResume(readCheckoutResume());
              }}
            />
          )}

          {needsShipping && (
            <Card className="bg-white border-stone/50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-semibold text-ink">Shipping Address</h3>
                  <Badge variant="outline" className="text-[9px]">Required for mailed certificates</Badge>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Street Address</Label>
                    <Input value={addr.line_1} onChange={(event) => setAddr({ ...addr, line_1: event.target.value })} placeholder="123 Main St" className="mt-1" disabled={inputsLocked} data-testid="addr-line1" />
                  </div>
                  <div>
                    <Label className="text-xs">Apt / Suite <span className="text-muted-foreground">(optional)</span></Label>
                    <Input value={addr.line_2} onChange={(event) => setAddr({ ...addr, line_2: event.target.value })} className="mt-1" disabled={inputsLocked} data-testid="addr-line2" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">City</Label>
                      <Input value={addr.city} onChange={(event) => setAddr({ ...addr, city: event.target.value })} className="mt-1" disabled={inputsLocked} data-testid="addr-city" />
                    </div>
                    <div>
                      <Label className="text-xs">State</Label>
                      <Select value={addr.state} onValueChange={(value) => setAddr({ ...addr, state: value })} disabled={inputsLocked}>
                        <SelectTrigger className="mt-1" data-testid="addr-state"><SelectValue placeholder="State" /></SelectTrigger>
                        <SelectContent>{US_STATES.map((state) => <SelectItem key={state} value={state}>{state}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">ZIP</Label>
                      <Input value={addr.postal_code} onChange={(event) => setAddr({ ...addr, postal_code: event.target.value })} className="mt-1" disabled={inputsLocked} data-testid="addr-zip" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-white border-stone/50">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-ink mb-4">Order Summary</h3>
              <div className="space-y-3" data-testid="checkout-order-summary">
                {items.map((item, index) => {
                  let price = item.base_price_cents || (item.service_family === 'authentication' ? 2000 : 5000);
                  if (item.is_expedited && item.service_family === 'authentication') price += 2000;
                  if (item.wants_mailed_document) price += 1000;

                  return (
                    <div key={`${item.item_title || 'item'}-${index}`} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.service_family === 'authentication'
                          ? <Shield className="h-3.5 w-3.5" style={{ color: '#0E6B5D' }} />
                          : <FileText className="h-3.5 w-3.5" style={{ color: '#A36A3A' }} />}
                        <span className="text-sm text-ink">{item.item_title || 'Untitled'}</span>
                        {item.is_expedited && <Badge variant="outline" className="text-[9px]">Expedited</Badge>}
                        {item.wants_mailed_document && <Badge variant="outline" className="text-[9px]"><Mail className="h-2.5 w-2.5 mr-0.5" />Mailed COA</Badge>}
                      </div>
                      <span className="text-sm text-ink">${(price / 100).toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              <Separator className="my-4" />
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>${(getSubtotal() / 100).toFixed(2)}</span></div>
                {couponDiscount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>-${(couponDiscount / 100).toFixed(2)}</span></div>}
                <div className="flex justify-between font-semibold text-base mt-2" data-testid="checkout-total-row"><span>Total</span><span>${(getTotal() / 100).toFixed(2)}</span></div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone/50">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-ink">Payment</h3>
                <Badge className="text-[9px]" style={paymentBadgeStyles(paymentMode)} data-testid="checkout-payment-badge">
                  {getPaymentModeLabel(paymentMode)}
                </Badge>
              </div>

              {/* Per-attempt provider selection (D2): show a chooser when both Stripe and
                  PayPal are offered. The choice is locked once a payment session exists.
                  Labels are provider-agnostic to the buyer — they see "Credit or debit card"
                  vs "PayPal", not "Stripe" (which is a backend implementation detail). */}
              {stripeAvailable && paypalAvailable && isAuthenticated && !paymentSession && (
                <div className="flex gap-2" data-testid="checkout-provider-selector">
                  <Button
                    type="button"
                    variant={selectedProvider === 'stripe' ? 'default' : 'outline'}
                    onClick={() => { setSelectedProvider('stripe'); handleCheckoutError(null); }}
                    className="flex-1 rounded-[12px] h-10"
                    data-testid="checkout-select-stripe"
                  >
                    Credit or debit card
                  </Button>
                  <Button
                    type="button"
                    variant={selectedProvider === 'paypal' ? 'default' : 'outline'}
                    onClick={() => { setSelectedProvider('paypal'); handleCheckoutError(null); }}
                    className="flex-1 rounded-[12px] h-10"
                    data-testid="checkout-select-paypal"
                  >
                    PayPal
                  </Button>
                </div>
              )}

              {!usesStripe && !usesPayPal && (
                <div className="p-4 rounded-lg" style={{ background: 'hsl(var(--bg-paper-2))' }} data-testid="checkout-mock-payment-panel">
                  <p className="text-xs text-muted-foreground text-center">Payment processing uses the mock driver in this environment.</p>
                </div>
              )}

              {usesStripe && !paymentSession && isAuthenticated && (
                <div className="rounded-xl border border-dashed border-stone/40 bg-stone-50 p-4 space-y-2" data-testid="checkout-payment-init-panel">
                  <p className="text-sm text-ink">We'll create your order first, then load the secure card payment form.</p>
                  <p className="text-xs text-muted-foreground">Your payment form will mount only after order and payment initialization succeed.</p>
                </div>
              )}

              {usesPayPal && !paymentSession && isAuthenticated && !requiresPasswordSetup && (
                <PayPalPaymentSection
                  config={config}
                  createPayPalOrder={createPayPalOrder}
                  onPaymentSuccess={handleSuccess}
                  onPaymentError={handleCheckoutError}
                />
              )}

              {!isAuthenticated && (
                <div className="rounded-xl border border-dashed border-stone/40 bg-stone-50 p-4 space-y-2" data-testid="checkout-auth-required-panel">
                  <p className="text-sm text-ink">Sign in to unlock order creation and payment.</p>
                  <p className="text-xs text-muted-foreground">Your cart and shipping details stay here while you authenticate.</p>
                </div>
              )}

              {requiresPasswordSetup && (
                <div className="rounded-xl border border-dashed border-stone/40 bg-stone-50 p-4 space-y-2" data-testid="checkout-password-required-panel">
                  <p className="text-sm text-ink">Save your new password to unlock checkout.</p>
                  <p className="text-xs text-muted-foreground">Once your password is set, you can continue straight into payment.</p>
                </div>
              )}

              {usesStripe && paymentSession && stripeReady && (
                <StripePaymentSection
                  paymentSession={paymentSession}
                  paymentMode={paymentMode}
                  stripePromise={stripePromise}
                  onPaymentSuccess={handleSuccess}
                  onPaymentError={handleCheckoutError}
                />
              )}

              {usesStripe && !stripeReady && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="checkout-stripe-unavailable-panel">
                  <p className="text-sm" style={{ color: '#5A1F2A' }}>Card payments are configured but the form could not be loaded.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {checkoutError && (
            <Card className="bg-white border-red-200" data-testid="checkout-error-banner">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-sm" style={{ color: '#5A1F2A' }} data-testid="checkout-error-alert">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{checkoutError}</span>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <Button variant="outline" onClick={() => navigate('/order/cart')} className="gap-2" data-testid="checkout-back-to-cart-button">
              <ArrowLeft className="h-4 w-4" /> Back to Cart
            </Button>

            {!paymentSession && !usesPayPal && (
              <Button
                onClick={handleInitializePayment}
                disabled={processing || authLoading || !isAuthenticated || requiresPasswordSetup || (usesStripe && !stripeReady)}
                className="gap-2 rounded-[12px] h-11 px-8 active:scale-[0.98]"
                data-testid="checkout-initialize-payment-button"
              >
                {processing
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Preparing payment...</>
                  : <><CheckCircle className="h-4 w-4" /> {!isAuthenticated ? 'Sign in to continue' : (requiresPasswordSetup ? 'Save password to continue' : (usesStripe ? 'Continue to Payment' : 'Place Order'))}</>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}